import {
  ColDef,
  GridReadyEvent,
  FirstDataRenderedEvent,
  IServerSideGetRowsParams,
  RowDoubleClickedEvent,
} from "ag-grid-community";
import { AgGridReact } from "ag-grid-react";
import { useCallback, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { usePromotionColumnDefs } from "./columnDefs";
import { dateFilterModel } from "../../../../utils/Formatter/AgGridFilter";
import AgGrid from "../../../UI/AgGrid";
import { GraphQLClient } from "graphql-request";
import { createGraphQLClientWithMiddleware } from "../../../../services/graphqlClient";
import { PROMOTION_AGGRID } from "../../../../services/AgGrid/MarketingAgGrid";
import { PromotionCategory } from "../../../../generated/marketing";

interface Props {
  categoryFilter?: PromotionCategory;
}

const PromotionTable = ({ categoryFilter }: Props) => {
  const gridRef = useRef<AgGridReact<any>>(null);
  const navigate = useNavigate();

  const columnDef: ColDef[] | undefined = usePromotionColumnDefs();

  const graphQLClientWithHeaderSales: GraphQLClient =
    createGraphQLClientWithMiddleware("marketing");

  const datasource = {
    async getRows(params: IServerSideGetRowsParams) {
      const { request } = params;
      const { startRow, endRow, filterModel, sortModel } = request;
      const {
        start_date,
        end_date,
        created_date,
        created_by_object,
        priority,
        receive_type,
        last_updated_date,
        use_auto,
        ...otherFilterModel
      } = filterModel;
      const formatFilter = {
        ...otherFilterModel,
        start_date: dateFilterModel(start_date),
        end_date: dateFilterModel(end_date),
        created_date: dateFilterModel(created_date),
        created_by: created_by_object,
        priority: priority
          ? {
              filter: isNaN(parseInt(priority.filter))
                ? 0
                : parseInt(priority.filter),
              filterType: "number",
              type: "equals",
            }
          : undefined,
        receive_type: receive_type
          ? {
              ...receive_type,
              values: receive_type.values.filter(
                (type: string) => type !== "freebie"
              ),
            }
          : undefined,
        last_updated_date: dateFilterModel(last_updated_date),
        use_auto:
          use_auto && use_auto.values?.length > 0
            ? {
                filterType: "setBoolean",
                values: use_auto.values.map((v: string) => v === "1"),
              }
            : undefined,
      };
      try {
        const { PromotionSettingViewsAggrid } =
          await graphQLClientWithHeaderSales.request(PROMOTION_AGGRID, {
            aggridInput: {
              startRow,
              endRow,
              filterModel: formatFilter,
              sortModel,
            },
          });
        params.success({
          rowData: PromotionSettingViewsAggrid.results as any[],
          rowCount: PromotionSettingViewsAggrid.count as number,
        });
      } catch (err) {
        params.fail();
      }
    },
  };

  const onFilterChanged = useCallback(
    (params: any) => {
      const instance = params.api.getFilterInstance("promotion_category");
      switch (categoryFilter) {
        case "discount_product":
          instance?.setModel({ values: ["discount_product"] });
          break;
        case "discount_fix_price":
          instance?.setModel({ values: ["discount_fix_price"] });
          break;
        case "discount_order":
          instance?.setModel({ values: ["discount_order"] });
          break;
        case "free_gift":
          instance?.setModel({ values: ["free_gift"] });
          break;
        default:
          instance?.setModel({});
          break;
      }
      params.api.onFilterChanged();
    },
    [categoryFilter]
  );

  const onGridReady = (params: GridReadyEvent) => {
    onFilterChanged(params);
    params.api.setServerSideDatasource(datasource);
  };

  const onFirstDataRendered = (params: FirstDataRenderedEvent) => {
    onFilterChanged(params);
  };

  useEffect(() => {
    if (gridRef.current && gridRef.current.api) {
      onFilterChanged(gridRef.current);
    }
  }, [gridRef, onFilterChanged]);

  const navigateHandler = (category: string) => {
    switch (category) {
      case "discount_product":
        return "discount-product";
      case "discount_total":
        return "discount-total";
      case "discount_fix_price":
        return "discount-fix-price";
      case "discount_order":
        return "discount-order";
      case "free_gift":
        return "free-gift";
    }
  };

  const onRowDoubleClicked = (params: RowDoubleClickedEvent) => {
    navigate(
      `/marketing/promotion/${navigateHandler(
        params.data.promotion_category
      )}/${encodeURIComponent(params.data.id)}`
    );
  };

  return (
    <AgGrid
      ref={gridRef}
      columnDefs={columnDef}
      height={665}
      onRowDoubleClicked={onRowDoubleClicked}
      onFirstDataRendered={onFirstDataRendered}
      onGridReady={onGridReady}
    />
  );
};

export default PromotionTable;
